import { useChatStore, type ChatStore } from "../Store";
import type { AwsRequestQuery } from "@/Modules/Chat/Models/Api/Chat";
import type { AllowedChatRoomItemType, ImageChatRoomItem } from "@/Modules/Chat/Models/Domain/Chat";
import { ChatRoomItemType, FileTypes } from "@/Modules/Chat/Models/Domain/Chat";
import {
  ChatRoomItemTypeMessageWebSocket,
  type ChatSendImageEventMessageSocket,
  MessageTypeWebSocket,
} from "@/Modules/Chat/Models/WebSocket";

import { awsUploadFileRequest } from "@/Services/TherapyApi/Chat";
import AwsFileConnector from "@/Services/FileService/AwsFileConnector";

import { hasValue, isObject } from "@/Utils/Objects";
import { imageToJpg, readAndCreateThumbnail } from "@/Utils/Image";
import { log } from "@/Utils/Debug";

const sendImageMessageActionHandler = async (
  chatId: string,
  fileId: string,
  fileType: string,
  height: number,
  width: number,
  size: number,
  thumbnail: string
): Promise<AllowedChatRoomItemType> => {
  const chatStore = useChatStore();

  return new Promise<AllowedChatRoomItemType>((resolve, reject) => {
    if (!hasValue(chatStore.rooms[chatId])) return reject();

    const createdAt = new Date();
    const sendTextEventPayload: ChatSendImageEventMessageSocket = {
      action: "",
      chat: chatId,
      content_type: fileType,
      created: createdAt.getTime(),
      etype: ChatRoomItemTypeMessageWebSocket.IMAGE,
      height,
      nonce: `chat:message:${chatId}:${chatStore.rooms[chatId].events.length}`,
      upload_id: fileId,
      width,
      thumbnail,
      extra: "",
      private: false,
      type: MessageTypeWebSocket.CHAT_ROOM_ITEM,
      size,
    };

    const event: ImageChatRoomItem = {
      id: sendTextEventPayload.nonce,
      url: "",
      old: true,
      createdAt,
      type: ChatRoomItemType.OUTGOING_TEXT,
      isConfirmed: false,
      isReceived: false,
      thumbnail: thumbnail,
      height,
      width,
    };

    chatStore.rooms[chatId].events.push(event);
    chatStore.socketManager?.sendMessage(sendTextEventPayload);

    resolve(event);
  });
};

const createThumbnailAndSendMessage = async (file: File | Blob, chatId: string, awsId: string) => {
  const thumbnailData = await readAndCreateThumbnail(file);

  if (isObject(thumbnailData)) {
    await sendImageMessageActionHandler(
      chatId,
      awsId,
      file.type,
      thumbnailData.height,
      thumbnailData.width,
      file.size,
      thumbnailData.thumbnail
    );
  }
};

const sendFileRequest = async (fileName: string, fileType: string) => {
  const awsRequestQuery: AwsRequestQuery = {
    content_type: fileType,
    file_name: fileName,
  };

  return awsUploadFileRequest(awsRequestQuery);
};

const handleError = (chatStore: ChatStore) => {
  chatStore.setFileProgress(100);
  chatStore.showFileError(FileTypes.IMAGE);
};

const _isJpgFile = (fileType: string) => {
  return fileType === "image/jpeg" || fileType === "image/jpg";
};

const _setFileNameJpg = (filename: string): string => {
  return filename.replace(/\.[^/.]+$/, ".jpeg");
};

const isValidSize = (maxSize: number, fileSize: number) => {
  return maxSize > fileSize;
};

const SendImageActionHandler = async (file: File, chatId: string) => {
  const chatStore = useChatStore();
  const awsFileConnector = new AwsFileConnector();

  chatStore.isUploadingFile = true;
  const resolveImage = (f: File): Promise<File | Blob | null> => {
    if (!_isJpgFile(f.type)) {
      return imageToJpg(f);
    }

    return Promise.resolve(f);
  };

  const uploadFile = await resolveImage(file);

  if (!hasValue(uploadFile)) {
    handleError(chatStore);
  } else {
    const fileName = _isJpgFile(file.type) ? file.name : _setFileNameJpg(file.name);

    try {
      const awsUploadRequestApiResponse = await sendFileRequest(fileName, uploadFile.type);

      if (isValidSize(awsUploadRequestApiResponse.max_size, uploadFile.size)) {
        await Promise.all([
          awsFileConnector.awsUploadFile(awsUploadRequestApiResponse, uploadFile, chatStore.setFileProgress),
          createThumbnailAndSendMessage(uploadFile, chatId, awsUploadRequestApiResponse.id),
        ]);
      } else {
        handleError(chatStore);
      }
    } catch (e) {
      handleError(chatStore);
      log(e);
    } finally {
      chatStore.isUploadingFile = false;
    }
  }
};

export default SendImageActionHandler;
